/**
 * Contact Center data fetching from Algolia.
 *
 * Fetches contact information (phone, email, hours) from the CONTACT_CENTER index.
 * Per-country contact details for customer service.
 *
 * Used by `getLayoutContactInfo()` (locale layout) and the ISR-cached API route
 * `/api/contact-info/[countryCode]`.
 */

import { BRAND_TRAITS } from '@/lib/brands/traits';
import { BRAND, type Brand } from '@/lib/cms/config';
import { devError, devLog, devWarn } from '@/lib/utils/devLog';
import { getAlgoliaClient } from './client';
import { ALGOLIA_CONFIG } from './config';

// =============================================================================
// Types
// =============================================================================

/**
 * Working hours interval (open/close times).
 */
export interface OpenInterval {
  start: string;
  end: string;
}

/**
 * Working hours for a specific day or category.
 */
export interface WorkingHours {
  sunday: OpenInterval[];
  monday: OpenInterval[];
  tuesday: OpenInterval[];
  wednesday: OpenInterval[];
  thursday: OpenInterval[];
  friday: OpenInterval[];
  saturday: OpenInterval[];
  holidays?: Array<{
    date: string;
    isClosed?: boolean;
    openIntervals: OpenInterval[];
  }>;
}

/**
 * Contact center hit from Algolia CONTACT_CENTER index.
 */
export interface ContactCenterHit {
  objectID: string;
  country_code: string;
  phone_number: string;
  email: string;
  hours: WorkingHours;
  /** Yext entity id when indexed by POS Manager (e.g. WW000000) */
  external_reference?: string;
}

/**
 * Normalized contact info returned to consumers.
 */
export interface ContactInfo {
  phoneNumber: string;
  email: string;
  weekdayHours: string;
  weekendHours: string;
  countryCode: string;
}

/** Yext worldwide contact-center entity (fallback when a country has no specific record). */
export const WORLDWIDE_CONTACT_ENTITY_ID = 'WW000000';

const CONTACT_ATTRIBUTES = ['phone_number', 'email', 'hours', 'country_code', 'external_reference'];

// =============================================================================
// Fallback Data
// =============================================================================

const BREITLING_FALLBACK_CONTACT: ContactInfo = {
  phoneNumber: '+41 32 654 54 54',
  email: 'contact@breitling.com',
  weekdayHours: 'Mon-Fri: 09:00-18:00',
  weekendHours: 'Sat-Sun: Closed',
  countryCode: 'CH',
};

const GALLET_FALLBACK_CONTACT: ContactInfo = {
  phoneNumber: '+41 32 654 54 54',
  email: 'info@gallet.com',
  weekdayHours: 'Mon-Fri: 09:00-17:00',
  weekendHours: 'Sat-Sun: Closed',
  countryCode: 'CH',
};

// UG publishes no phone or opening hours — contact runs through the site's form
// — so only the general-inquiry address is set.
const UG_FALLBACK_CONTACT: ContactInfo = {
  phoneNumber: '',
  email: 'contact@universalgeneve.com',
  weekdayHours: '',
  weekendHours: '',
  countryCode: 'CH',
};

const FALLBACK_CONTACT_BY_BRAND: Record<Brand, ContactInfo> = {
  gallet: GALLET_FALLBACK_CONTACT,
  breitling: BREITLING_FALLBACK_CONTACT,
  ug: UG_FALLBACK_CONTACT,
};

export const FALLBACK_CONTACT: ContactInfo = FALLBACK_CONTACT_BY_BRAND[BRAND];

// =============================================================================
// Helpers
// =============================================================================

/**
 * Format working hours for display.
 * Converts the hours object to readable weekday/weekend strings.
 * Handles various data formats from Algolia.
 */
function formatHours(hours: WorkingHours | string | undefined): {
  weekdayHours: string;
  weekendHours: string;
} {
  if (!hours) {
    return {
      weekdayHours: FALLBACK_CONTACT.weekdayHours,
      weekendHours: FALLBACK_CONTACT.weekendHours,
    };
  }

  // If hours is a string, return it directly
  if (typeof hours === 'string') {
    return {
      weekdayHours: hours,
      weekendHours: '',
    };
  }

  // Format a single day's hours - handle various formats
  const formatDayHours = (intervals: OpenInterval[] | string | undefined): string => {
    // Handle undefined/null
    if (!intervals) {
      return 'Closed';
    }
    // Handle string format (e.g., "09:00-18:00")
    if (typeof intervals === 'string') {
      return intervals || 'Closed';
    }
    // Handle empty array
    if (!Array.isArray(intervals) || intervals.length === 0) {
      return 'Closed';
    }
    // Handle array of intervals
    return (
      intervals
        .map((i) => {
          if (typeof i === 'string') return i;
          if (i && typeof i === 'object' && i.start && i.end) {
            return `${i.start}-${i.end}`;
          }
          return '';
        })
        .filter(Boolean)
        .join(', ') || 'Closed'
    );
  };

  // Get Monday hours as representative for weekdays
  const mondayHours = formatDayHours(hours.monday);
  const saturdayHours = formatDayHours(hours.saturday);
  const sundayHours = formatDayHours(hours.sunday);

  // Format weekday hours (assume Mon-Fri are same)
  const weekdayHours = mondayHours === 'Closed' ? 'Mon-Fri: Closed' : `Mon-Fri: ${mondayHours}`;

  // Collapse to "Sat-Sun: X" when both days match so the i18n pipeline in ContactUs.tsx
  // (which only translates the literal "Sat-Sun") still works for non-English locales.
  const weekendHours =
    saturdayHours === sundayHours
      ? `Sat-Sun: ${saturdayHours}`
      : `Sat: ${saturdayHours}, Sun: ${sundayHours}`;

  return { weekdayHours, weekendHours };
}

function hasPhone(hit: ContactCenterHit | undefined): hit is ContactCenterHit {
  return Boolean(hit?.phone_number?.trim());
}

async function searchContactCenter(filters: string): Promise<ContactCenterHit | undefined> {
  const client = getAlgoliaClient({ ssr: true });
  const response = await client.searchSingleIndex<ContactCenterHit>({
    indexName: ALGOLIA_CONFIG.getContactCenterIndex(),
    searchParams: {
      query: '',
      filters,
      attributesToRetrieve: CONTACT_ATTRIBUTES,
      hitsPerPage: 1,
    },
  });
  return response.hits?.[0];
}

function toContactInfo(hit: ContactCenterHit, countryCode: string): ContactInfo {
  const { weekdayHours, weekendHours } = formatHours(hit.hours);
  return {
    phoneNumber: hit.phone_number,
    email: hit.email || FALLBACK_CONTACT.email,
    weekdayHours,
    weekendHours,
    countryCode,
  };
}

function worldwideContactFilters(): string {
  return `objectID:${WORLDWIDE_CONTACT_ENTITY_ID} OR external_reference:${WORLDWIDE_CONTACT_ENTITY_ID}`;
}

// =============================================================================
// Main Fetch Function
// =============================================================================

/**
 * Fetch contact info for a country from Algolia CONTACT_CENTER index.
 *
 * Lookup order: country-specific hit with phone → Yext WW000000 → static fallback.
 *
 * This is called by the ISR API route and layout prefetch, not directly by components.
 * Results are cached at the API route / layout level for 24 hours.
 *
 * @param countryCode - ISO country code (e.g., 'US', 'CH', 'GB')
 * @returns Contact info or fallback if not found
 */
export async function fetchContactInfo(countryCode: string): Promise<ContactInfo> {
  const normalizedCode = countryCode.toUpperCase();

  if (!BRAND_TRAITS.usesAlgoliaContactCentre) {
    devWarn(
      '[fetchContactInfo] Skipped: this brand has no Algolia contact-centre record, so there is nothing to look up. Contact details for it must come from the CMS.',
      { countryCode: normalizedCode }
    );
    return { ...FALLBACK_CONTACT, countryCode: normalizedCode };
  }

  try {
    devLog(
      `[fetchContactInfo] Fetching for ${normalizedCode} from ${ALGOLIA_CONFIG.getContactCenterIndex()}`
    );

    const countryHit = await searchContactCenter(`country_code:${normalizedCode}`);
    if (hasPhone(countryHit)) {
      return toContactInfo(countryHit, normalizedCode);
    }

    devLog(
      `[fetchContactInfo] No country phone for ${normalizedCode}, trying ${WORLDWIDE_CONTACT_ENTITY_ID}`
    );

    const worldwideHit = await searchContactCenter(worldwideContactFilters());
    if (hasPhone(worldwideHit)) {
      return toContactInfo(worldwideHit, normalizedCode);
    }

    devLog(
      `[fetchContactInfo] No Algolia contact data for ${normalizedCode}, using static fallback`
    );
    return { ...FALLBACK_CONTACT, countryCode: normalizedCode };
  } catch (error) {
    devError('[fetchContactInfo] Error fetching contact info:', error);
    return { ...FALLBACK_CONTACT, countryCode: normalizedCode };
  }
}
